/**
 * Shared by delivery-backend and the delivery frontend: catalog annotations that tie a
 * Component to its part of the app-bobbee monorepo, and the API response types.
 * The convention is documented in docs/catalog-annotations.md.
 */

/** Comma-separated folder prefixes of the component in the monorepo, e.g. `aws-lambda/src/main-api`. */
export const SOURCE_PATHS_ANNOTATION = 'bobbee.io/source-paths';
/** Comma-separated GitHub workflow files that build or check the component. */
export const CI_WORKFLOWS_ANNOTATION = 'bobbee.io/ci-workflows';
/** Comma-separated deployed Lambda function names without the `<stage>-bobbeeapp-` prefix, e.g. `main-api`. */
export const LAMBDA_FUNCTIONS_ANNOTATION = 'bobbee.io/lambda-functions';

type Annotated = { metadata: { annotations?: Record<string, string> } };

/** Values of a comma-separated annotation, trimmed, empty ones dropped. */
export function annotationList(
  entity: Annotated,
  annotation: string,
): string[] {
  return (entity.metadata.annotations?.[annotation] ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
}

/** Folder prefixes normalised to end with `/`, so `api` does not match `api-v2/…`. */
export function sourcePrefixes(entity: Annotated): string[] {
  return annotationList(entity, SOURCE_PATHS_ANNOTATION).map(p =>
    p.replace(/^\/+/, '').replace(/\/*$/, '/'),
  );
}

export type ChecksState =
  | 'SUCCESS'
  | 'FAILURE'
  | 'PENDING'
  | 'ERROR'
  | 'EXPECTED'
  | null;
export type ReviewDecision =
  | 'APPROVED'
  | 'CHANGES_REQUESTED'
  | 'REVIEW_REQUIRED'
  | null;

export interface PullRequestSummary {
  number: number;
  title: string;
  url: string;
  author: string | null;
  draft: boolean;
  createdAt: string;
  updatedAt: string;
  reviewDecision: ReviewDecision;
  checks: ChecksState;
  /** How many changed files fall under the component's source paths. */
  matchingFiles: number;
}

export interface CiRun {
  workflow: string;
  runId: number;
  name: string;
  branch: string | null;
  status: string;
  conclusion: string | null;
  url: string;
  actor: string | null;
  createdAt: string;
}

export interface EnvironmentDeployment {
  /** GitHub environment, e.g. `deploy.prod`. */
  environment: string;
  /** Git ref the deploy ran on: the version tag, e.g. `v2.18.22`. */
  version: string;
  /** Commit deployed (the tag's commit). */
  sha: string;
  deployedAt: string;
  deployedBy: string | null;
  runUrl: string | null;
  /**
   * The latest deployment newer than this one that failed: the environment kept running this
   * version, or was updated outside the deploy workflow (GitHub cannot tell).
   */
  failed?: FailedDeployment | null;
}

export interface FailedDeployment {
  version: string;
  sha: string;
  failedAt: string;
  runUrl: string | null;
}

export interface DiffCommit {
  sha: string;
  message: string;
  author: string | null;
  date: string | null;
  url: string;
}

export interface TicketCount {
  key: string;
  commits: number;
}

/** What `from` has and `to` does not (e.g. staging vs prod: what the next prod deploy ships). */
export interface EnvironmentDiff {
  from: string;
  to: string;
  aheadBy: number;
  /** At most 250 commits (GitHub compare limit): see `aheadBy` for the real count. */
  commits: DiffCommit[];
  tickets: TicketCount[];
  computedAt: string;
}

export interface ReleasesResponse {
  /** `owner/name` of the monorepo, to link commits. */
  repo: string;
  environments: EnvironmentDeployment[];
  diffs: EnvironmentDiff[];
}

export interface TicketLocation {
  key: string;
  /** Commits of the default branch that mention the key. */
  commits: DiffCommit[];
  environments: Array<{
    environment: string;
    version: string | null;
    present: boolean;
  }>;
}

/** The GitHub release of a version tag: its changelog as GitHub generated it. */
export interface ReleaseNotes {
  name: string;
  url: string;
  /** Markdown of the changelog, without the generator's HTML comment. */
  body: string;
  prerelease: boolean;
  /** When the release (and the tag) was created, and when it was published. */
  createdAt: string;
  publishedAt: string | null;
  author: string | null;
}

/** A section of the changelog (`### 🚀 Features`) and its pull requests. */
export interface ChangelogSection {
  title: string;
  pullRequests: number;
}

/** The first successful deployment of the version to an environment. */
export interface VersionDeployment {
  environment: string;
  deployedAt: string;
  deployedBy: string | null;
  runUrl: string | null;
  /** Successful deployments of the version to the environment, redeployments included. */
  deploys: number;
  /** The environment still runs the version. */
  current: boolean;
}

/** A Jira ticket of the version: cited by its changelog, or shipped to production by it. */
export interface VersionTicket {
  key: string;
  summary: string | null;
  issueType: string | null;
  team: string | null;
  status: string | null;
  storyPoints: number | null;
  /** A hotfix ticket (Jira hotfix filter), with its priority. */
  hotfix: boolean;
  priority: string | null;
  inChangelog: boolean;
  /** First production deployment holding the ticket, whatever the version; null when none. */
  prodVersion: string | null;
  prodAt: string | null;
  /** When this version first shipped it to production: ticket opened → prod, develop → prod. */
  openToProdHours: number | null;
  developToProdHours: number | null;
}

/** What GitHub's compare of the previous version with this one tells. */
export interface VersionSize {
  commits: number;
  files: number;
  additions: number;
  deletions: number;
  /** GitHub lists 300 files at most: files and lines are then a floor. */
  truncated: boolean;
}

/** The CI/CD of the pull requests of the changelog (those merged into a synced base branch). */
export interface VersionCi {
  /** Pull requests of the changelog, and those the portal knows. */
  pullRequests: number;
  known: number;
  /** Over the pull requests whose runs are counted; null when none is. */
  runs: number | null;
  failedRuns: number | null;
  /** Over the pull requests whose cost is computed; null when none is. */
  costUsd: number | null;
  costed: number;
  checkRuns: number;
  ephemeralDeploys: number;
}

/** One version (tag): its changelog, where it went and what it shipped. */
export interface VersionDetail {
  /** `owner/name` of the monorepo. */
  repo: string;
  version: string;
  /** Null when the tag has no GitHub release, or when GitHub could not be read (`githubError`). */
  notes: ReleaseNotes | null;
  /** Why GitHub was not read: no credentials on the portal, or a failure; null when it was. */
  githubError: 'unconfigured' | 'unavailable' | null;
  /** The version the changelog compares with (`Full Changelog`), and that compare on GitHub. */
  previousVersion: string | null;
  compareUrl: string | null;
  sections: ChangelogSection[];
  /** Pull requests listed by the changelog, and the people who wrote them (a count only). */
  pullRequests: number;
  contributors: number;
  newContributors: number;
  size: VersionSize | null;
  /** The release radar's environments, newest code first (dev, staging, prod). */
  environments: string[];
  /** The environments the version reached, in that order. */
  deployments: VersionDeployment[];
  /**
   * Its production deployment, when it has one: the MEP of its release or a fix, and the version
   * that replaced it (null while it still runs).
   */
  prod: {
    deployedAt: string;
    mep: boolean;
    previous: { version: string; deployedAt: string } | null;
    next: { version: string; deployedAt: string } | null;
    /** Tickets it shipped to production first; null while its commits are not analysed. */
    tickets: number | null;
  } | null;
  tickets: VersionTicket[];
  ci: VersionCi;
}

/** Jira keys of the given projects mentioned in a text, upper-cased and de-duplicated. */
export function jiraKeys(text: string, projects: string[]): string[] {
  if (projects.length === 0) return [];
  const pattern = new RegExp(
    `\\b(?:${projects
      .map(p => p.replace(/[^A-Za-z0-9]/g, ''))
      .join('|')})-\\d+\\b`,
    'gi',
  );
  return [...new Set((text.match(pattern) ?? []).map(k => k.toUpperCase()))];
}

/** Project of a bare ticket number when `delivery.jiraProjects` is not set. */
export const DEFAULT_JIRA_PROJECT = 'MP';

/**
 * The Jira key typed or pasted by a user, upper-cased: `1234` (in `project`), `mp1234`,
 * `MP 1234`, a Jira URL, a branch (`feat/mp-1234-ocr`) or an env host (`mp-1234.feature.bobbee.co`).
 * Null when no key is found.
 */
export function normalizeTicketKey(
  raw: string,
  project: string = DEFAULT_JIRA_PROJECT,
): string | null {
  const text = raw.trim();
  if (/^\d+$/.test(text)) return `${project}-${text}`.toUpperCase();
  const compact = /^([A-Za-z]+)\s*(\d+)$/.exec(text);
  if (compact) return `${compact[1]}-${compact[2]}`.toUpperCase();
  const key = /(?:^|[^A-Za-z0-9])([A-Za-z][A-Za-z0-9]*-\d+)(?![0-9])/.exec(
    text,
  );
  return key ? key[1].toUpperCase() : null;
}

export interface FlakyTest {
  spec: string;
  title: string;
  /** Runs of the window that executed the test. */
  runs: number;
  failures: number;
  /** Percentage of failed executions. */
  failRate: number;
  /** Commits on which the test both passed and failed: the strongest flakiness signal. */
  flakyCommits: number;
  lastFailure: { at: string; runUrl: string } | null;
}

export interface FlakyTestsResponse {
  since: string;
  /** E2E runs in the window. */
  runs: number;
  flaky: FlakyTest[];
  /** Failed every time in the window: broken rather than flaky. */
  alwaysFailing: FlakyTest[];
  weekly: Array<{ week: string; flakyTests: number }>;
}

/** Median and 90th percentile of durations in hours (null when there is nothing to measure). */
export interface DurationStats {
  count: number;
  median: number | null;
  p90: number | null;
}

export interface HotfixItem {
  key: string;
  summary: string;
  priority: string | null;
  team: string | null;
  status: string;
  createdAt: string;
  /**
   * Became a hotfix: its creation, or its change into type HOTFIX (a Bug qualified at triage).
   * Every duration and the SLA start here.
   */
  hotfixAt: string;
  /** Hotfix → left "new" (picked up); 0 when it was already picked up. */
  pickUpHours: number | null;
  /** Hotfix → first "done" category status (fix validated, ready for prod). */
  fixHours: number | null;
  /** Hotfix → first successful deploy.prod deployment containing the key: the main indicator. */
  toProdHours: number | null;
  /** When that deployment finished, and its version (tag). */
  prodDeployedAt: string | null;
  prodVersion: string | null;
  reopenCount: number;
  /** Open tickets: hours since they became a hotfix. */
  ageHours: number | null;
  slaHours: number;
  /** Open past its SLA, or resolved later than it. */
  overSla: boolean;
}

export interface HotfixPeriodStats {
  /** Tickets that became hotfixes in the period. */
  opened: number;
  /** Hotfixes deployed to production in the period. */
  resolved: number;
  pickUp: DurationStats;
  fix: DurationStats;
  toProd: DurationStats;
  /** Percentage of the period's resolved hotfixes that met their SLA. */
  withinSla: number | null;
}

export interface HotfixGroupStats {
  name: string;
  resolved: number;
  toProd: DurationStats;
  withinSla: number | null;
  open: number;
  slaHours?: number;
}

export interface HotfixesResponse {
  days: number;
  since: string;
  current: HotfixPeriodStats;
  /** The same number of days just before, for comparison. */
  previous: HotfixPeriodStats;
  byPriority: HotfixGroupStats[];
  byTeam: HotfixGroupStats[];
  /** Every team with a tracked hotfix, whatever the team filter: the options of the filter. */
  teams: string[];
  /** Last 12 months, by month of production deployment. */
  monthly: Array<{ month: string; resolved: number; toProd: DurationStats }>;
  open: HotfixItem[];
  /** Fixed and validated ("done" status), waiting for the production deploy. */
  awaitingProd: HotfixItem[];
  /** Done in Jira during the period, but no production deployment mentions the key. */
  unmatched: HotfixItem[];
  /** How many hotfixes done in Jira during the period a production deployment explains. */
  coverage: {
    matched: number;
    unmatched: number;
    trackingSince: string | null;
  };
  /** Deployed to production in the period, most recent first (at most 30). */
  resolved: HotfixItem[];
  syncedAt: string | null;
}

/** Median and 85th percentile of durations in hours (null when there is nothing to measure). */
export interface LeadTimeStats {
  count: number;
  p50: number | null;
  p85: number | null;
}

/**
 * A feature = a Jira ticket whose pull requests were merged into the base branch (develop).
 * Never holds a person: the sync does not store pull request authors.
 */
export interface FeatureItem {
  key: string;
  /** Jira data; null while the ticket is not read from Jira yet (or does not exist). */
  summary: string | null;
  issueType: string | null;
  team: string | null;
  /** Jira ticket creation. */
  openedAt: string | null;
  /** Oldest commit of the feature's pull requests (author date). */
  firstCommitAt: string;
  /** First and last pull request merged into the base branch. */
  firstMergedAt: string;
  lastMergedAt: string;
  pullRequests: number;
  branches: number;
  /** Ticket opened → first commit (null when unknown; 0 when the first commit predates the ticket). */
  openToFirstCommitHours: number | null;
  /** First commit → last merge into the base branch: the lead time. */
  leadTimeHours: number;
  /** Ticket opened → last merge. */
  openToDevelopHours: number | null;
}

export interface FeaturesResponse {
  days: number;
  since: string;
  /** Merged in the window, most recently merged first (at most 200). */
  features: FeatureItem[];
  /** Matching features of the window, before the 200 limit. */
  total: number;
  /** Every team and issue type seen: the options of the filters, whatever the active ones. */
  teams: string[];
  issueTypes: string[];
  syncedAt: string | null;
}

/**
 * A ticket and the merged pull requests behind it, without their authors. `feature` is null for a
 * ticket fixed by hotfixes only (merged into main, never into develop): it has no lead time.
 */
export interface FeatureDetail {
  /** Jira data; null while the ticket is not read from Jira yet (or does not exist). */
  ticket: Pick<
    FeatureItem,
    'key' | 'summary' | 'issueType' | 'team' | 'openedAt'
  >;
  feature: FeatureItem | null;
  pullRequests: Array<{
    number: number;
    title: string;
    url: string;
    branch: string;
    /** Branch it was merged into: develop, or main for a hotfix. */
    base: string;
    openedAt: string;
    /** Oldest commit of the pull request (author date). */
    firstCommitAt: string | null;
    mergedAt: string;
    /** Workflow runs of its branch while it lived; null while not counted yet. */
    ciRuns: number | null;
    failedRuns: number | null;
    /** CI/CD cost in USD (check runs and ephemeral deployments at their unit cost); null while not computed or merged before the EC2 fleet. */
    ciCost: number | null;
    /** Runs of the pull request checks and deployments of the branch to an ephemeral environment; null like `ciCost`. */
    checkRuns: number | null;
    ephemeralDeploys: number | null;
  }>;
}

export interface TeamLeadTimes {
  /** First commit → last merge into the base branch. */
  leadTime: LeadTimeStats;
  /** Ticket opened → first commit. */
  openToFirstCommit: LeadTimeStats;
  /** Ticket opened → last merge. */
  openToDevelop: LeadTimeStats;
}

export interface TeamStats extends TeamLeadTimes {
  name: string;
  /** Features merged in the window. */
  features: number;
  pullRequests: number;
  /** Mean pull requests / branches per feature, one decimal. */
  pullRequestsPerFeature: number;
  branchesPerFeature: number;
  /** Percentiles are hidden (null) below `minSample` features: too few to not point at someone. */
  belowMinSample: boolean;
}

export interface TeamsResponse {
  days: number;
  since: string;
  minSample: number;
  /** All the teams together, or the filtered team. */
  overall: TeamLeadTimes & { features: number; pullRequests: number };
  /** The same number of days just before, for comparison. */
  previous: TeamLeadTimes & { features: number; pullRequests: number };
  teams: TeamStats[];
  /** Last 12 months, by month of the last merge. */
  monthly: Array<{ month: string; features: number; leadTime: LeadTimeStats }>;
  /** Every team and issue type seen: the options of the filters, whatever the active ones. */
  teamNames: string[];
  issueTypes: string[];
  /**
   * From when the synced data is complete (the first sync read `backfillDays` back): the
   * months before it are left out, and null when unknown.
   */
  coveredSince?: string | null;
  /** Whether the period compared with is covered: deltas are hidden otherwise. */
  previousCovered?: boolean;
  syncedAt: string | null;
}

/** A Jira sprint of the portal's board (one cadence for every team). */
export type SprintState = 'closed' | 'active' | 'future';

export interface SprintInfo {
  id: number;
  name: string;
  state: SprintState;
  startDate: string | null;
  endDate: string | null;
}

/** Tickets finished in one sprint, per Jira issue type. */
export interface SprintBucket extends SprintInfo {
  /**
   * Counted from the Jira sprint report (what Jira's report and velocity chart show); otherwise
   * estimated from the tickets' current status and latest sprint (report not read).
   */
  fromJiraReport: boolean;
  total: number;
  byType: Record<string, number>;
  /** Velocity: story points of the tickets finished, unestimated ones counting 0. */
  points: number;
  pointsByType: Record<string, number>;
  /** Tickets finished with story points, out of `total`. */
  estimated: number;
  /** Distinct pull requests merged into develop that cite the tickets of the bucket. */
  pullRequests: number;
  /** Their distinct branches. */
  branches: number;
  /** Workflow runs of those pull requests, counted ones only (`ciCounted` of `pullRequests`). */
  ciRuns: number;
  failedRuns: number;
  ciCounted: number;
  /** CI/CD cost in USD of the pull requests whose cost is computed (`ciCostCounted`). */
  ciCost: number;
  ciCostCounted: number;
  /** Their runs of the pull request checks and deployments to an ephemeral environment. */
  checkRuns: number;
  ephemeralDeploys: number;
  /** The cost split: those runs and deployments at their unit costs (`SprintsResponse.unitCosts`). */
  checkCostUsd: number;
  ephemeralCostUsd: number;
}

/** Tickets finished per sprint by one team, aligned with `SprintsResponse.sprints`. */
export interface TeamSprintSeries {
  name: string;
  total: number;
  perSprint: number[];
  /** Story points finished in the window, and per sprint (velocity). */
  points: number;
  pointsPerSprint: number[];
}

/** The sprints the productivity tabs can show as their period, newest first. */
export interface SprintPeriodsResponse {
  sprints: SprintInfo[];
}

export interface SprintsResponse {
  /** The last sprints (closed or running), oldest first, counting the filtered team and type only. */
  sprints: SprintBucket[];
  /** Every issue type by overall frequency: the order of the colors, stable under any filter. */
  types: string[];
  /** Every team with its tickets per sprint (the type filter applies, the team filter does not). */
  teams: TeamSprintSeries[];
  /** Every team and issue type seen: the options of the filters. */
  teamNames: string[];
  /**
   * Finished tickets of the window that sit in no sprint (kanban, hotfix): counted by resolution
   * date, in the sprints without Jira sprint report only (the report leaves them out).
   */
  outsideSprint: number;
  /** USD of EC2 of one CI run and one ephemeral build (`delivery.productivity.ciCost`). */
  unitCosts: { checkRunUsd: number; ephemeralDeployUsd: number };
  /**
   * A sprint asked for (`from`, `to`) is unknown or older than the synced data: another window
   * is shown instead, and the page says so.
   */
  adjusted?: boolean;
  syncedAt: string | null;
}

/** A finished Jira ticket, with what the pull requests behind it say (null when it has none). */
export interface SprintTicket {
  key: string;
  summary: string | null;
  issueType: string | null;
  team: string | null;
  status: string;
  resolvedAt: string | null;
  /** The sprint it was finished in; `outside` when it has none and is placed by resolution date. */
  sprint: { id: number; name: string; outside: boolean } | null;
  pullRequests: number;
  branches: number;
  /** Workflow runs of its pull requests' branches, null while none is computed yet. */
  ciRuns: number | null;
  failedRuns: number | null;
  /** CI/CD cost in USD of its pull requests, null while none is computed. */
  ciCost: number | null;
  /** Runs of the pull request checks and ephemeral deployments of its branches, null like `ciCost`. */
  checkRuns: number | null;
  ephemeralDeploys: number | null;
  /** First commit → last merge into develop. */
  leadTimeHours: number | null;
  /** Story points, null when not estimated. */
  storyPoints: number | null;
}

export interface SprintDetailsResponse {
  scope: {
    team: string | null;
    /** Comma-separated issue types, null for all. */
    type: string | null;
    /** One sprint, or null for the whole window. */
    sprint: SprintInfo | null;
    /** The sprints of the window, oldest first. */
    window: SprintInfo[];
    /** A sprint asked for is unknown or older than the synced data: another window is shown. */
    adjusted?: boolean;
  };
  totals: {
    tickets: number;
    withPullRequests: number;
    pullRequests: number;
    branches: number;
    ciRuns: number;
    failedRuns: number;
    /** Pull requests whose workflow runs are counted, out of `pullRequests`. */
    ciCounted: number;
    /** CI/CD cost in USD, over `ciCostCounted` pull requests. */
    ciCost: number;
    ciCostCounted: number;
    checkRuns: number;
    ephemeralDeploys: number;
    checkCostUsd: number;
    ephemeralCostUsd: number;
    leadTime: LeadTimeStats;
    /** Story points of the tickets, and how many tickets are estimated. */
    points: number;
    estimated: number;
  };
  byType: Array<{ type: string; count: number }>;
  types: string[];
  /** Every team seen in the tickets: the options of the team filter. */
  teamNames: string[];
  /** Most recently finished first (at most 500). */
  tickets: SprintTicket[];
  syncedAt: string | null;
}

/** A ticket that reached production: its first `deploy.prod` deployment holding its key. */
export interface ShippedTicket {
  key: string;
  summary: string | null;
  issueType: string | null;
  team: string | null;
  openedAt: string | null;
  /** Last pull request merged into develop; null for a change that never went through develop. */
  mergedAt: string | null;
  prodAt: string;
  version: string;
  /** Last merge into develop → production. */
  developToProdHours: number | null;
  /** Ticket opened → production. */
  openToProdHours: number | null;
}

/** Merged into develop, not in production since. */
export interface PendingTicket {
  key: string;
  summary: string | null;
  issueType: string | null;
  team: string | null;
  /** Jira status, null when the ticket is not read from Jira yet. */
  status: string | null;
  mergedAt: string;
  /** Hours since the merge into develop. */
  waitingHours: number;
  /** Already deployed to staging (in the staging → prod diff of the release radar). */
  inStaging: boolean;
}

export interface ProdRelease {
  version: string;
  deployedAt: string;
  /** Tickets it shipped first (no sub-task); null while its commits are not analysed yet. */
  tickets: number | null;
  /**
   * The MEP of its release (its first deployment, or a redeployment within a day), else a fix
   * (patch) of the release.
   */
  mep: boolean;
}

export interface ProdPeriodStats {
  /** Tickets whose first production deployment falls in the period. */
  tickets: number;
  developToProd: LeadTimeStats;
  openToProd: LeadTimeStats;
}

/** The product as a whole: a deployment has no team, so the team and type filters do not apply. */
export interface ProdCadence {
  deploys: number;
  previousDeploys: number;
  deploysPerWeek: number;
  previousDeploysPerWeek: number;
  /**
   * « Tickets arrivés en prod » split between the MEPs (first deployment of a release and its
   * redeployments within a day) and the fixes that shipped them: they add up to `current.tickets`.
   */
  mepTickets: number;
  fixTickets: number;
  /** Releases whose MEP is among the deployments, e.g. `v2.19`; fix deployments. */
  mepReleases: string[];
  fixes: number;
}

export interface ProdResponse {
  days: number;
  since: string;
  minSample: number;
  /**
   * When the period is a sprint: the number of its release (Sprint 19 → 19), whose MEP counts in
   * the sprint whenever deployed, with the fixes deployed during the sprint's dates; null for a
   * number of days.
   */
  release: { current: number; previous: number | null } | null;
  current: ProdPeriodStats;
  /** The same number of days just before, for comparison. */
  previous: ProdPeriodStats;
  /** False when production tracking started after the previous period began: no comparison. */
  previousCovered: boolean;
  cadence: ProdCadence;
  /** Weeks of the period (Monday, UTC), oldest first: tickets shipped and deployments. */
  weekly: Array<{ week: string; tickets: number; deploys: number }>;
  /** Last 12 months, by month of the production deployment. */
  monthly: Array<{ month: string; tickets: number; leadTime: LeadTimeStats }>;
  /** Shipped in the period, most recent first (at most 200). */
  shipped: ShippedTicket[];
  shippedTotal: number;
  /** Waiting for production now, whatever the period, longest wait first (at most 200). */
  pending: PendingTicket[];
  pendingTotal: number;
  /** Deployments of the period, most recent first (at most 50). */
  releases: ProdRelease[];
  /** First production deployment known: nothing can be matched before. */
  trackingSince: string | null;
  teamNames: string[];
  issueTypes: string[];
  syncedAt: string | null;
}

/** Where the time of a team's tickets goes: the median of each phase, measured apart. */
export interface FlowPhases {
  /** A team, or « Toutes les équipes » for the first row. */
  name: string;
  /** Tickets merged into develop in the period. */
  tickets: number;
  /** Ticket opened → first commit. */
  waiting: LeadTimeStats;
  /** First commit → last merge into develop. */
  coding: LeadTimeStats;
  /** Last merge into develop → production, for those already in production. */
  release: LeadTimeStats;
}

/** Tickets of the running sprint per Jira status category. */
export interface SprintProgress {
  name: string;
  todo: number;
  inProgress: number;
  done: number;
}

/** A ticket in progress (status category « En cours »). */
export interface InProgressTicket {
  key: string;
  summary: string | null;
  issueType: string | null;
  team: string | null;
  status: string | null;
  /** When it entered « En cours »; null when Jira did not say. */
  since: string | null;
  sinceHours: number | null;
  /** In progress for longer than the p85 lead time of its team (or of everyone). */
  slow: boolean;
  sprints: number;
}

export interface CarryOverRow {
  name: string;
  /** Finished tickets. */
  done: number;
  /** Of them, the ones that went through two sprints or more. */
  carried: number;
}

/** A pull request still open, without its author. */
export interface AgingPullRequest {
  number: number;
  title: string;
  url: string;
  /** Team of the ticket its title cites; « Sans ticket » when it cites none. */
  team: string;
  keys: string[];
  openedAt: string;
  ageHours: number;
  reviewDecision: ReviewDecision;
}

export interface AgeBuckets {
  name: string;
  /** Open for less than a day, 1–3 days, 3–7 days, more than 7 days. */
  under1d: number;
  d1to3: number;
  d3to7: number;
  over7d: number;
}

export interface FlowResponse {
  days: number;
  since: string;
  minSample: number;
  phases: FlowPhases[];
  activeSprint: SprintInfo | null;
  sprintProgress: SprintProgress[];
  /** Longest in progress first (at most 200). */
  inProgress: InProgressTicket[];
  inProgressTotal: number;
  /** The sprints that started in the period, oldest first. */
  carryOverBySprint: Array<CarryOverRow & { sprint: SprintInfo }>;
  carryOverByTeam: CarryOverRow[];
  /** Tickets of the period's sprints that went through the most sprints (at most 20). */
  mostCarried: InProgressTicket[];
  /** Non-draft pull requests open now, per team and age. */
  openPullRequests: AgeBuckets[];
  /** Oldest first (at most 30). */
  oldestPullRequests: AgingPullRequest[];
  teamNames: string[];
  issueTypes: string[];
  /**
   * From when the synced data is complete (the first sync read `backfillDays` back): the
   * months before it are left out, and null when unknown.
   */
  coveredSince?: string | null;
  /** Whether the period compared with is covered: deltas are hidden otherwise. */
  previousCovered?: boolean;
  syncedAt: string | null;
}

/** Workflow runs of the pull requests merged into develop, counted ones only. */
export interface CiFailureStats {
  /** Pull requests whose runs are counted. */
  pullRequests: number;
  runs: number;
  failed: number;
  /** Percentage of failed runs, null without runs. */
  failureRate: number | null;
}

/** Tickets created and finished in a period, with the story points of the finished ones. */
export interface MixPeriod {
  created: number;
  done: number;
  bugShare: number | null;
  points: number;
  /** Percentage of the finished tickets with story points, null when none finished. */
  estimatedShare: number | null;
}

export interface MixResponse {
  days: number;
  since: string;
  /** Every issue type by overall frequency: the order of the colors, stable under any filter. */
  types: string[];
  /** Issue types counted as bugs (`delivery.productivity.bugTypes`). */
  bugTypes: string[];
  /** Last 12 months: tickets finished per type, by resolution month. */
  monthly: Array<{
    month: string;
    total: number;
    byType: Record<string, number>;
  }>;
  /** Tickets finished in the period per team and type, and their story points. */
  byTeam: Array<{
    name: string;
    total: number;
    byType: Record<string, number>;
    points: number;
  }>;
  /** Weeks of the period (Monday, UTC): tickets created and finished. */
  weekly: Array<{ week: string; created: number; done: number }>;
  current: MixPeriod;
  previous: MixPeriod;
  hotfixes: {
    current: HotfixPeriodStats;
    previous: HotfixPeriodStats;
    byTeam: HotfixGroupStats[];
  };
  ci: {
    current: CiFailureStats;
    previous: CiFailureStats;
    /** Last 12 months, by month of the merge into develop. */
    monthly: Array<CiFailureStats & { month: string }>;
  };
  teamNames: string[];
  issueTypes: string[];
  /**
   * From when the synced data is complete (the first sync read `backfillDays` back): the
   * months before it are left out, and null when unknown.
   */
  coveredSince?: string | null;
  /** Whether the period compared with is covered: deltas are hidden otherwise. */
  previousCovered?: boolean;
  syncedAt: string | null;
}

/** The headline figures of a period, for the summary. */
export interface SummaryKpis {
  /** Tickets whose first production deployment falls in the period. */
  shipped: number;
  /** Median ticket opened → production, in hours. */
  openToProd: number | null;
  /** Median first commit → last merge into develop, in hours. */
  leadTime: number | null;
  /** Percentage of bugs in the tickets finished. */
  bugShare: number | null;
  /** Story points of the tickets finished (velocity of the period). */
  points: number;
  /** Percentage of the tickets finished with story points. */
  estimatedShare: number | null;
  /** Percentage of the hotfixes deployed within their SLA. */
  hotfixWithinSla: number | null;
  /** Production deployments per week, product-wide. */
  deploysPerWeek: number;
}

/** One team across the indicators of the other tabs; null when hidden or nothing to measure. */
export interface SummaryTeam {
  name: string;
  /** Tickets finished in the period, and their story points. */
  done: number;
  points: number;
  /** Median first commit → develop, hours. */
  leadTime: number | null;
  /** Median develop → production, hours. */
  developToProd: number | null;
  /** Percentage of the finished tickets carried over (two sprints or more). */
  carriedOver: number | null;
  bugShare: number | null;
  /** Hotfixes open now. */
  openHotfixes: number;
}

/**
 * Targets of the headline indicators (`delivery.productivity.targets`), set by the teams: a tile
 * says whether its figure meets its target. Unset: no target shown.
 */
export interface ProductivityTargets {
  /** Median first commit → develop, at most, in hours. */
  leadTimeHours?: number;
  /** Median Jira opening → production, at most, in hours. */
  openToProdHours?: number;
  /** Share of bugs among finished tickets, at most, in percent. */
  bugShare?: number;
  /** Share of hotfixes handled within their SLA, at least, in percent. */
  hotfixWithinSla?: number;
  /** Production deployments per week, at least. */
  deploysPerWeek?: number;
}

export interface SummaryResponse {
  days: number;
  since: string;
  minSample: number;
  /** The targets of the indicators, empty when none is set. */
  targets?: ProductivityTargets;
  /** The selected team, or all of them. */
  current: SummaryKpis;
  previous: SummaryKpis;
  /** False when production tracking started after the previous period began: the production figures of `previous` are not comparable. */
  previousProdCovered: boolean;
  /** Every team, whatever the team filter. */
  teams: SummaryTeam[];
  teamNames: string[];
  /**
   * From when the synced data is complete (the first sync read `backfillDays` back): the
   * months before it are left out, and null when unknown.
   */
  coveredSince?: string | null;
  /** Whether the period compared with is covered: deltas are hidden otherwise. */
  previousCovered?: boolean;
  syncedAt: string | null;
}

/** Where a data source stands: `never` = never ran on this database. */
export type SyncState = 'never' | 'running' | 'success' | 'error';

/** One data source the delivery plugin keeps in its database, and how its last run went. */
export interface SyncSourceStatus {
  /** Id of the scheduled task. */
  id: string;
  title: string;
  /** What it reads, and from where. */
  description: string;
  /** How often it refreshes by itself, e.g. « toutes les 15 min ». */
  frequency: string;
  state: SyncState;
  startedAt: string | null;
  finishedAt: string | null;
  /** While running: how far along, `total` null when it cannot be known in advance. */
  progress: { done: number; total: number | null; label: string | null } | null;
  /** How the last successful run ended, e.g. « 601 PR, 577 features ». */
  result: string | null;
  /** Why the last run failed. */
  error: string | null;
}

export interface SyncStatusResponse {
  sources: SyncSourceStatus[];
  /** Whether the caller may reload the sources now (platform admins). */
  canReload?: boolean;
}

/** What happened to one source when a reload was asked. */
export type SyncReloadOutcome =
  | 'started'
  | 'already-running'
  /** Refreshed less than a minute ago. */
  | 'recent'
  | 'unknown';

export interface SyncReloadResponse {
  results: Array<{ source: string; outcome: SyncReloadOutcome }>;
}

export * from './observability';
